import React from 'react';
import { useParams } from 'react-router';
import classNames from 'classnames';

import { AboutLayoutTypesClassic } from '../../../../types/template-layouts';
import { getDefaultModuleAlignment, getTextValue } from '../../../../utils/utils';
import { ParamsProps } from '../../live-event';
import { ContentEditors, ModuleMap, PageModule, PageModuleType } from '../../../../types/working-model';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../utils';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import '../../../../scss/live-event/base/modules/about-us.scss';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../hooks/use-translation-route';

interface AboutProps {
	module: PageModule;
	template: string;
	isLandingPage?: boolean;
}

const About = React.forwardRef<HTMLDivElement, AboutProps>(({ module, template }, ref) => {
	const params: ParamsProps = useParams();
	const language = params?.language || 'en';
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	const { content, styling_overrides } = module;
	const layout_type = content?.layout_type || AboutLayoutTypesClassic.standard;
	const {
		backgroundColor,
		backgroundImg,
		titleOverrides,
		descriptionOverrides,
		customHeadingOverrides
	} = getStylingOverrides(styling_overrides);
	const imagesLength = 1;

	const renderGallery = () => {
		const gallery = [];
		for (let i = 0; i < imagesLength; i += 1) {
			gallery.push(<img key={i} src={content.image} alt="" aria-hidden="true" width="275" height="220" />);
		}

		return gallery;
	};

	const { custom_heading, description, title } = getTextValue(content, language);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.about]}
			className={classNames(
				'about-us',
				`about-us-${layout_type}`,
				template,
				backgroundColor
			)}

			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<div className={classNames("about-us-text", { 'quill-editor': contentEditor === ContentEditors.quill })}>
					<TypographyItem className={classNames('evt-caption-1 about-us-custom-heading', customHeadingOverrides)} tagName="p">
						{t(`about.${module.id}.heading`, t('about.heading', custom_heading))}
					</TypographyItem>
					<div className="about-us-text-inner">
						<TypographyItem className={classNames('evt-heading-2 about-us-title', titleOverrides)} tagName="h2">
							{t(`about.${module.id}.title`, t('about.title', title))}
						</TypographyItem>
						<TypographyItem className="evt-body-text-1 about-body-text">
							<RenderEditorDescription
								description={t(`about.${module.id}.description`, t('about.description', description))}
								descriptionOverrides={classNames(descriptionOverrides)}
								useNew={contentEditor === ContentEditors.quill}
								defaultTextAlignment={getDefaultModuleAlignment(module, template)}
							/>
						</TypographyItem>
					</div>
				</div>
				<div className={classNames('about-us-gallery-wrapper', `about-us-gallery-wrapper-${imagesLength}`)}>
					<div className={classNames('about-us-gallery', `about-us-gallery-${imagesLength}`)}>
						{renderGallery()}
					</div>
				</div>
			</div>
		</section>
	);
});

About.displayName = 'About';

export default About;
